import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Col,
  Select,
  Space,
  Switch
} from "antd";
import { GoogleOutlined, WindowsFilled } from "@ant-design/icons";
import { Error, Success } from "../../../commons/Messages/Messages";
import { mailApi, getErrorMessage, startMailOAuth } from "./mailApi";
import { MAIL_PROVIDERS, detectProvider } from "./mailProviders";
import { useMailTranslation } from "./useMailTranslation";

const ServerFields = ({ prefix, t }) => (
  <Row gutter={[12, 0]}>
    <Col xs={16} md={18}>
      <Form.Item
        name={[prefix, "host"]}
        label={t("Server")}
        rules={[{ required: true, message: t("Required") }]}
      >
        <Input
          placeholder={prefix === "imap" ? "imap.example.com" : "smtp.example.com"}
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
      </Form.Item>
    </Col>
    <Col xs={8} md={6}>
      <Form.Item
        name={[prefix, "port"]}
        label={t("Port")}
        rules={[{ required: true, message: t("Required") }]}
      >
        <InputNumber min={1} max={65535} inputMode="numeric" style={{ width: "100%" }} />
      </Form.Item>
    </Col>
    <Col xs={16} md={16}>
      <Form.Item
        name={[prefix, "user"]}
        label={t("Username")}
        rules={[{ required: true, message: t("Required") }]}
      >
        <Input autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
      </Form.Item>
    </Col>
    <Col xs={8} md={8}>
      <Form.Item
        name={[prefix, "secure"]}
        label={t("SSL/TLS")}
        valuePropName="checked"
        tooltip={t("Turn off for STARTTLS ports such as 587 or 143.")}
      >
        <Switch />
      </Form.Item>
    </Col>
  </Row>
);

const OAUTH_ICONS = { google: <GoogleOutlined />, microsoft: <WindowsFilled /> };
// Password presets whose provider also offers the recommended OAuth sign-in.
const OAUTH_FOR_PRESET = { gmail: "google", outlook: "microsoft" };

const MailAccountModal = ({ open, account, onClose, onSaved, onDeleted }) => {
  const { t } = useMailTranslation();
  const [form] = Form.useForm();
  const [providerKey, setProviderKey] = useState("custom");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [oauthProviders, setOauthProviders] = useState([]);
  const [redirecting, setRedirecting] = useState(null);
  const isEdit = Boolean(account);
  const isOAuth = account?.authType === "oauth2";
  const provider = MAIL_PROVIDERS.find(item => item.key === providerKey);

  useEffect(() => {
    if (!open) return;
    mailApi
      .getOAuthProviders()
      .then(res => setOauthProviders(res.data || []))
      .catch(() => setOauthProviders([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTestResult(null);
    setRedirecting(null);
    form.resetFields();
    if (account) {
      setProviderKey("custom");
      form.setFieldsValue({
        email: account.email,
        displayName: account.displayName,
        signature: account.signature,
        imap: account.imap,
        smtp: account.smtp
      });
    } else {
      setProviderKey("custom");
      form.setFieldsValue({
        imap: MAIL_PROVIDERS.at(-1).imap,
        smtp: MAIL_PROVIDERS.at(-1).smtp
      });
    }
  }, [open, account]);

  const applyProvider = (key, email = form.getFieldValue("email")) => {
    const selected = MAIL_PROVIDERS.find(item => item.key === key);
    setProviderKey(key);
    const domain = (email || "").split("@")[1];
    const guess = prefix =>
      key === "custom" && domain ? `${prefix}.${domain}` : selected[prefix].host;
    form.setFieldsValue({
      imap: { ...selected.imap, host: guess("imap"), user: email || "" },
      smtp: { ...selected.smtp, host: guess("smtp"), user: email || "" }
    });
  };

  const handleEmailChange = event => {
    if (isEdit) return;
    const email = event.target.value.trim();
    const detected = detectProvider(email);
    applyProvider(detected?.key || providerKey, email);
  };

  // Leaves the app for the provider's consent page; MailOAuthCallback finishes the flow.
  const handleOAuth = async (providerKey, loginHint) => {
    try {
      setRedirecting(providerKey);
      await startMailOAuth(providerKey, loginHint);
    } catch (error) {
      setRedirecting(null);
      setTestResult({
        type: "error",
        message: getErrorMessage(error, t("Failed to start sign-in"))
      });
    }
  };

  const oauthButton = (item, label, loginHint) => (
    <Button
      key={item.key}
      block
      size="large"
      icon={OAUTH_ICONS[item.key]}
      loading={redirecting === item.key}
      disabled={Boolean(redirecting) && redirecting !== item.key}
      onClick={() => handleOAuth(item.key, loginHint)}
    >
      {label}
    </Button>
  );

  const buildBody = values => {
    if (isOAuth) {
      return { displayName: values.displayName, signature: values.signature || "" };
    }
    const smtpPassword = values.smtpPassword || values.password || "";
    return {
      email: values.email,
      displayName: values.displayName,
      signature: values.signature || "",
      imap: { ...values.imap, password: values.password || "" },
      smtp: { ...values.smtp, password: smtpPassword }
    };
  };

  const handleTest = async () => {
    try {
      const values = await form.validateFields();
      setTesting(true);
      setTestResult(null);
      await mailApi.testAccount({ ...buildBody(values), accountId: account?._id });
      setTestResult({
        type: "success",
        message: t("Connection successful. IMAP and SMTP are working.")
      });
    } catch (error) {
      if (error?.errorFields) return;
      setTestResult({
        type: "error",
        message: getErrorMessage(error, t("Connection test failed"))
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setTestResult(null);
      const body = buildBody(values);
      const res = isEdit
        ? await mailApi.updateAccount(account._id, body)
        : await mailApi.createAccount(body);
      Success(isEdit ? t("Mailbox updated") : t("Mailbox connected"));
      onSaved(res.data);
    } catch (error) {
      if (error?.errorFields) return;
      setTestResult({
        type: "error",
        message: getErrorMessage(error, t("Failed to save mailbox"))
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await mailApi.deleteAccount(account._id);
      Success(t("Mailbox disconnected"));
      onDeleted(account._id);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to disconnect mailbox")));
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t("Mailbox settings") : t("Connect a mailbox")}
      onCancel={onClose}
      width={640}
      className="mail-account-modal"
      destroyOnHidden
      footer={
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <div>
            {isEdit && (
              <Popconfirm
                title={t("Disconnect this mailbox?")}
                description={t("Emails stay on your mail server. Only the connection is removed.")}
                onConfirm={handleDelete}
                okText={t("Disconnect")}
                cancelText={t("Cancel")}
              >
                <Button danger>{t("Disconnect")}</Button>
              </Popconfirm>
            )}
          </div>
          <Space>
            {!isOAuth && (
              <Button onClick={handleTest} loading={testing}>
                {t("Test connection")}
              </Button>
            )}
            <Button type="primary" onClick={handleSave} loading={saving}>
              {isEdit ? t("Save") : t("Connect")}
            </Button>
          </Space>
        </Space>
      }
    >
      {!isEdit && oauthProviders.length > 0 && (
        <>
          <Space direction="vertical" style={{ width: "100%" }}>
            {oauthProviders.map(item =>
              oauthButton(item, t("Sign in with {{provider}}", { provider: item.label }))
            )}
          </Space>
          <p className="mail-muted mt-2 mb-0">
            {t("Recommended for Gmail, Google Workspace, Outlook and Microsoft 365.")}
          </p>
          <Divider plain>{t("or connect with a password")}</Divider>
        </>
      )}
      {isOAuth && (
        <Alert
          className="mb-3"
          type={account.lastError ? "warning" : "success"}
          showIcon
          message={t("Connected via {{provider}} sign-in", {
            provider: account.oauth?.provider === "microsoft" ? "Microsoft" : "Google"
          })}
          description={account.lastError ? t(account.lastError) : null}
          action={
            oauthProviders.some(item => item.key === account.oauth?.provider) &&
            oauthButton({ key: account.oauth.provider }, t("Reconnect"), account.email)
          }
        />
      )}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="email"
          label={t("Email address")}
          rules={[{ required: true, type: "email", message: t("Enter a valid email address") }]}
        >
          <Input
            disabled={isEdit}
            onChange={handleEmailChange}
            placeholder="name@company.de"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item name="displayName" label={t("Sender name")}>
          <Input placeholder={t("Shown to recipients, e.g. your company name")} />
        </Form.Item>
        {!isOAuth && !isEdit && (
          <Form.Item label={t("Provider")}>
            <Select
              value={providerKey}
              onChange={key => applyProvider(key)}
              options={MAIL_PROVIDERS.map(item => ({
                value: item.key,
                label: item.key === "custom" ? t("Other (manual settings)") : item.label
              }))}
            />
          </Form.Item>
        )}
        {!isOAuth && (
          <Form.Item
            name="password"
            label={t("Password")}
            rules={isEdit ? [] : [{ required: true, message: t("Required") }]}
            extra={isEdit ? t("Leave blank to keep the current password.") : null}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        )}
        {!isEdit && oauthProviders.some(item => item.key === OAUTH_FOR_PRESET[providerKey]) ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t("This provider may reject passwords. Signing in above is more reliable.")}
          />
        ) : (
          !isOAuth &&
          provider?.hint && (
            <Alert type="info" showIcon message={t(provider.hint)} style={{ marginBottom: 16 }} />
          )
        )}

        <Collapse
          ghost
          defaultActiveKey={isEdit && !isOAuth ? ["servers"] : []}
          items={[
            !isOAuth && {
              key: "servers",
              label: t("Server settings"),
              forceRender: true,
              children: (
                <>
                  <h6 className="mb-2">{t("Incoming mail (IMAP)")}</h6>
                  <ServerFields prefix="imap" t={t} />
                  <h6 className="mb-2 mt-2">{t("Outgoing mail (SMTP)")}</h6>
                  <ServerFields prefix="smtp" t={t} />
                  <Form.Item
                    name="smtpPassword"
                    label={t("SMTP password")}
                    extra={t("Only needed if it differs from the IMAP password.")}
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                </>
              )
            },
            {
              key: "signature",
              label: t("Signature"),
              forceRender: true,
              children: (
                <Form.Item
                  name="signature"
                  extra={t("Added to new messages, replies and forwards.")}
                >
                  <Input.TextArea rows={4} placeholder={t("Best regards")} />
                </Form.Item>
              )
            }
          ].filter(Boolean)}
        />
        {testResult && <Alert type={testResult.type} showIcon message={testResult.message} />}
      </Form>
    </Modal>
  );
};

export default MailAccountModal;
